import { useCallback, useRef, useState } from "react";
import { useMotionValue } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { PersonTile } from "@/components/PersonTile";
import { PersonDialog } from "@/components/PersonDialog";
import { PEOPLE } from "@/data/people";

const VISIBLE = PEOPLE.filter((p) => p.featured);

export const PeopleStage = () => {
  const { t } = useLang();
  const trackRef = useRef(null);
  const mouseX = useMotionValue(null);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  const onMove = useCallback(
    (e) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      mouseX.set(e.clientX - rect.left + track.scrollLeft);
    },
    [mouseX]
  );

  const scrollBy = (dir) => trackRef.current?.scrollBy({ left: dir * trackRef.current.clientWidth * 0.6, behavior: "smooth" });

  const step = (dir) => {
    const i = VISIBLE.findIndex((p) => p.id === selected?.id);
    setSelected(VISIBLE[(i + dir + VISIBLE.length) % VISIBLE.length]);
  };

  return (
    <section id="people" data-testid="people-section" className="relative overflow-hidden bg-ink pt-24 md:pt-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading
          eyebrow={t.people.eyebrow}
          title={t.people.title}
          titleItalic={t.people.titleItalic}
          description={t.people.description}
        />
        <Reveal delay={0.2}>
          <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.25em] text-ash/80" data-testid="people-hint">
            <span className="hidden lg:inline [@media(pointer:coarse)]:hidden">{t.people.hint}</span>
            <span className="lg:hidden [@media(pointer:coarse)]:inline">{t.people.hintTouch}</span>
          </p>
        </Reveal>
      </div>

      {/* The stage: pure black so the cut-out portraits and their reflections blend seamlessly */}
      <div className="relative mt-12 bg-black md:mt-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-ink to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-44 bg-gradient-to-t from-ink via-black/90 to-transparent" />
        {/* Soft stage vignette at the edges (kept off the tiles so seams never show) */}
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-black to-transparent md:w-32" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-black to-transparent md:w-32" />

        <ul
          ref={trackRef}
          onMouseMove={onMove}
          onMouseLeave={() => mouseX.set(null)}
          data-testid="people-track"
          className="no-scrollbar relative flex h-[520px] snap-x snap-mandatory items-end overflow-x-auto overflow-y-hidden px-5 pb-6 pt-10 [perspective:1400px] sm:h-[580px] md:h-[640px] lg:justify-center lg:px-8 xl:h-[700px]"
        >
          {VISIBLE.map((person, i) => (
            <PersonTile
              key={person.id}
              person={person}
              index={i}
              mouseX={mouseX}
              hovered={hovered === person.id}
              anyHovered={hovered !== null}
              onHover={setHovered}
              onOpen={setSelected}
            />
          ))}
        </ul>

        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label={t.people.prev}
          data-testid="people-scroll-prev"
          className="absolute left-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-black/70 text-cream backdrop-blur transition-colors duration-300 hover:border-ember hover:text-ember lg:hidden"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label={t.people.next}
          data-testid="people-scroll-next"
          className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-line bg-black/70 text-cream backdrop-blur transition-colors duration-300 hover:border-ember hover:text-ember lg:hidden"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <PersonDialog person={selected} onClose={() => setSelected(null)} onPrev={() => step(-1)} onNext={() => step(1)} />
    </section>
  );
};
